/**
 * Standardized TanStack Query Keys
 *
 * Centralizes all query keys for consistency and type safety.
 * Prevents duplicate keys and makes cache invalidation easier.
 *
 * Usage:
 * ```ts
 * const { data } = useQuery({
 *   queryKey: queryKeys.jobs.active(assistantId),
 *   queryFn: ...
 * });
 * ```
 */

// Type imports for query key parameters
import type { AssistantSearchParams } from '@/shared/services/AssistantService';

export const queryKeys = {
  /**
   * Upload Jobs
   */
  jobs: {
    // All active jobs for an assistant
    active: (assistantId: number) => ['jobs', 'active', assistantId] as const,

    // Single job by ID
    byId: (jobId: string) => ['jobs', 'byId', jobId] as const,

    // All jobs (active + completed)
    all: (assistantId: number) => ['jobs', 'all', assistantId] as const,

    // Dismissed job externalIds (user-cleared, persisted across refreshes)
    dismissed: (assistantId: number) =>
      ['jobs', 'dismissed', assistantId] as const,
  },

  /**
   * Knowledge Base Documents
   * Supports caching for instant display on page load
   */
  kb: {
    // Document list for an assistant (supports 'temp-assistant' string)
    items: (assistantId: number | string) =>
      ['kb', 'items', assistantId] as const,

    // Single document by ID
    item: (documentId: number) => ['kb', 'item', documentId] as const,

    // Document preview URL (for in-browser preview)
    itemPreview: (documentId: number | string) =>
      ['kb', 'itemPreview', documentId] as const,

    // Document search results (supports 'temp-assistant' string)
    search: (assistantId: number | string, query: string) =>
      ['kb', 'search', assistantId, query] as const,

    // All KB documents (for invalidation)
    all: () => ['kb'] as const,
  },

  /**
   * Upload Progress State (Scoped by assistantId)
   * Used for client-side state that needs persistence across navigation
   */
  uploadProgress: {
    // Polled documents for an assistant (client-side cache)
    polledDocuments: (assistantId: number) =>
      ['uploadProgress', 'polledDocuments', assistantId] as const,

    // Documents being deleted (client-side tracking)
    deletingDocuments: (assistantId: number) =>
      ['uploadProgress', 'deletingDocuments', assistantId] as const,
  },

  /**
   * Browse State (Scoped by assistantId)
   * Persists browse position/pagination when navigating away
   */
  browse: {
    // Accumulated browse items and pagination state
    state: (assistantId: number) => ['browse', 'state', assistantId] as const,
  },

  /**
   * Worker Health
   */
  health: {
    // Worker health status
    status: () => ['health', 'status'] as const,
  },

  /**
   * User Data
   */
  user: {
    // All user data (for broad invalidation)
    all: () => ['user'] as const,

    // User by ID
    byId: (uid: string) => ['user', uid] as const,
  },

  /**
   * Assistants (Django backend)
   */
  assistants: {
    // All assistants (for broad invalidation)
    all: () => ['assistants'] as const,

    // Filtered list of assistants
    list: (params?: AssistantSearchParams) => ['assistants', params] as const,

    // Paginated assistants (for infinite scroll)
    paginated: (params?: Omit<AssistantSearchParams, 'page'>) =>
      ['assistants-paginated', params] as const,

    // Single assistant by ID
    detail: (assistantId: number) => ['assistant', assistantId] as const,

    // Single assistant by UID (for edit mode)
    detailByUid: (uid: string) => ['assistant', 'byUid', uid] as const,

    // Favourited assistants (independent fetch for carousel)
    favourited: () => ['assistants-favourited'] as const,

    // Distinct workspace names (for filter dropdown)
    workspaces: () => ['assistants-workspaces'] as const,
  },

  /**
   * User Recents
   */
  userRecents: {
    // All user recents (for broad invalidation)
    all: () => ['user-recents'] as const,

    // User recents by user ID
    byUser: (uid: string) => ['user-recents', uid] as const,
  },

  /**
   * User Favourites
   */
  userFavourites: {
    // All user favourites (for broad invalidation)
    all: () => ['user-favourites'] as const,

    // User favourites by user ID
    byUser: (uid: string) => ['user-favourites', uid] as const,
  },

  /**
   * Recent Assistants (sidebar display)
   */
  recentAssistants: {
    // All recent assistants (for broad invalidation)
    all: () => ['recentAssistants'] as const,

    // Recent assistants by user ID
    byUser: (uid: string) => ['recentAssistants', uid] as const,
  },

  /**
   * Bots (legacy MongoDB)
   */
  bots: {
    // All bots (for broad invalidation)
    all: () => ['bots'] as const,

    // All bots list
    allBots: () => ['bots', 'all'] as const,

    // User's bots
    userBots: (uid: string) => ['bots', 'user', uid] as const,

    // Single bot details
    details: (botId: string) => ['bot', botId] as const,

    // Bots by workspace
    workspace: (workspace: string) => ['bots', 'workspace', workspace] as const,
  },

  /**
   * AI Models
   */
  aiModels: {
    // All AI models
    all: () => ['aimodels'] as const,

    // AI model options (for select components)
    options: () => ['aimodels', 'options'] as const,

    // Legacy key (for backward compatibility)
    legacy: () => ['ai-models'] as const,
  },

  /**
   * Departments
   */
  departments: {
    // All departments (for broad invalidation)
    all: () => ['departments'] as const,

    // Department options (for select components)
    options: (tenantId?: string | null) =>
      ['departments', 'options', tenantId] as const,

    // Departments by tenant
    byTenant: (tenantId: string) => ['departments', tenantId] as const,
  },

  /**
   * Documents (Django backend)
   */
  documents: {
    // All documents (for broad invalidation)
    all: () => ['django-documents'] as const,

    // Documents by assistant
    byAssistant: (assistantId: string | number) =>
      ['django-documents', assistantId] as const,
  },

  /**
   * Conversations
   */
  conversations: {
    // All conversations (for broad invalidation)
    all: () => ['conversations'] as const,

    // Conversations by user
    byUser: (uid: string) => ['conversations', uid] as const,
  },

  /**
   * Permissions (Assistant access control)
   */
  permissions: {
    // All permissions (for broad invalidation)
    all: () => ['permissions'] as const,

    // Users with access to an assistant
    users: (assistantId: string) =>
      ['permissions', 'users', assistantId] as const,

    // All system users (for inviting)
    allUsers: () => ['permissions', 'allUsers'] as const,
  },

  /**
   * Cloud Import Jobs (Scoped by assistantId)
   * Tracks active cloud import job for each assistant separately
   */
  cloudJobs: {
    // Active cloud job for an assistant (client-side state)
    active: (assistantId: string) =>
      ['cloudJobs', 'active', assistantId] as const,
  },
} as const;

/**
 * Query configuration defaults
 *
 * Used by TanStack Query for all queries
 */
export const queryConfig = {
  // How long data stays fresh before refetching
  staleTime: 5 * 60 * 1000, // 5 minutes

  // How long unused data stays in cache
  gcTime: 10 * 60 * 1000, // 10 minutes (renamed from cacheTime in v5)

  // Retry configuration
  retry: 3,
  retryDelay: (attemptIndex: number) =>
    Math.min(1000 * 2 ** attemptIndex, 30000),
};
